/*
 * Copyright (c) 2023-2024 SmartWifi - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential. No warranty, explicit or implicit provided. In
 * no event will the author be liable for any claim or damages.
 */

import { useEffect, useState } from "react";
import { backgroundConfigToStyle } from "../layouts/LayoutPortalUtil";
import CaptivePortalSplashScreen from "./CaptivePortalSplashScreen";
import LoadingSpinner from "../../elements/LoadingSpinner";

const CaptivePortalContainer = ({
  children,
  layout,
  dimension,
  fields = {},
  showSplashScreen = true,
  absolutePosition = true,
  interactable = true,
  onSplashScreenComplete,
  zoomedText = false,
}) => {
  const { splashScreenUrl, splashScreenEnabled } = fields;

  // handle splash screen state
  const initialShowChildren = !showSplashScreen || !splashScreenEnabled || splashScreenUrl == null;
  const [showChildren, setShowChildren] = useState(initialShowChildren);

  // handle background image load if needed
  const [imageLoading, setImageLoading] = useState(false);
  const background = layout?.background;
  
  useEffect(() => {
    // if not showing splash screen, send callback
    if (!showSplashScreen || !splashScreenEnabled) { 
      onSplashScreenComplete?.(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // return if no background image
    if (background?.image != null || background?.type !== "image") {
      return;
    }

    // set as loading
    setImageLoading(true);

    // prepare callback for when image loads
    const img = new Image();
    img.onload = () => {
      setImageLoading(false);
    };
    img.onerror = () => {
      setImageLoading(false);
    };
    img.src = background.image;
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [background?.image]);

  // check layout provided
  if (layout == null) {
    return children;
  }

  // container style
  const containerStyle = {
    overflow: (dimension == null || zoomedText) ? "auto" : "hidden",
    ...(!interactable) && { pointerEvents: "none" },
    ...backgroundConfigToStyle(layout?.background, dimension, absolutePosition)
  };

  return (
    <div className="layer-container" style={containerStyle}>
      {imageLoading && (
        <LoadingSpinner />
      )}
      <div className="force-fill-container" style={{ visibility: showChildren ? "visible" : "hidden" }}>
        {children}
      </div>
      {(showSplashScreen && splashScreenEnabled) && (
        <CaptivePortalSplashScreen
          url={splashScreenUrl}
          onFadeOut={() => setShowChildren(true)}
          onError={() => setShowChildren(true)}
          onCompletion={() => onSplashScreenComplete?.(true)}
        />
      )}
    </div>
  );
};

export default CaptivePortalContainer;
